import React, { useMemo, useState } from "react";
import {
    Avatar,
    Badge,
    Box,
    Popover,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
    IconButton,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import CloseIcon from "@mui/icons-material/Close";
import { FaSearch } from "react-icons/fa";

const AVATAR_SIZE = 28;
const INLINE_MAX = 3; // avatars shown inline before +N

const colorFromNameKey = (key) => {
    const s = String(key || "?").toLowerCase();
    let hash = 0;
    for (let i = 0; i < s.length; i += 1) {
        hash = s.charCodeAt(i) + ((hash << 5) - hash);
    }
    const h = Math.abs(hash) % 360;
    return `hsl(${h}, 75%, 48%)`;
};

const buildInitials = (firstName, lastName, email) => {
    const f = (firstName || "").trim();
    const l = (lastName || "").trim();
    if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
    if (f.length >= 2) return f.slice(0, 2).toUpperCase();
    if (f) return `${f[0]}`.toUpperCase();
    const em = (email || "").trim();
    if (em.length >= 2) return em.slice(0, 2).toUpperCase();
    if (em) return `${em[0]}`.toUpperCase();
    return "?";
};

const avatarBg = (firstName = "", lastName = "", email = "") => {
    const fromParts = `${firstName || ""} ${lastName || ""}`.trim();
    const initials = buildInitials(firstName, lastName, email);
    const nameKeyForColor = (fromParts || email || initials || "?").trim();
    return colorFromNameKey(nameKeyForColor);
};

/** Normalize an assigner object coming from the facet API */
const normAssigner = (a) => ({
    id: String(a.id || a.userId || ""),
    firstName: a.first_name || a.firstName || "",
    lastName: a.last_name || a.lastName || "",
    email: a.email || a.Email || "",
    employeeCode: a.employee_code || a.employeeCode || "",
});

// ─────────────────────────────────────────────────────────────────────────────
// Sub-component: single clickable avatar with blue-ring selection
// ─────────────────────────────────────────────────────────────────────────────
const PersonAvatar = ({ person, isSelected, onClick, disabled, size = AVATAR_SIZE }) => {
    const initials = buildInitials(person.firstName, person.lastName, person.email);
    const name = `${person.firstName || ""} ${person.lastName || ""}`.trim() || person.email;
    const bg = avatarBg(person.firstName, person.lastName, person.email);

    return (
        <Tooltip title={name || person.email} arrow placement="top">
            <Avatar
                onClick={disabled ? undefined : onClick}
                sx={{
                    width: size,
                    height: size,
                    fontSize: size <= 28 ? "0.68rem" : "0.85rem",
                    fontWeight: 700,
                    bgcolor: bg,
                    color: "#fff",
                    cursor: disabled ? "default" : "pointer",
                    outline: isSelected ? "2px solid #2563eb" : "none",
                    outlineOffset: "2px",
                    transition: "outline 0.15s",
                    "&:hover": disabled ? {} : { filter: "brightness(1.12)" },
                }}
            >
                {initials}
            </Avatar>
        </Tooltip>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Assigner picker popover
// ─────────────────────────────────────────────────────────────────────────────
const AssignerPickerPopover = ({ open, anchorEl, onClose, assigners, selectedId, currentUserId, onSelect }) => {
    const [search, setSearch] = useState("");

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return assigners.filter((a) => {
            const name = `${a.firstName} ${a.lastName}`.toLowerCase();
            return !q || name.includes(q) || a.email.toLowerCase().includes(q);
        });
    }, [assigners, search]);

    const handleSelect = (id) => {
        if (selectedId != null && String(selectedId) === String(id)) {
            onSelect(null);
        } else {
            onSelect(id);
        }
        onClose();
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: "12px",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        overflow: "hidden",
                        width: 280,
                        border: "1px solid #f1f5f9",
                        mt: 0.5,
                    },
                },
            }}
        >
            <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.25 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", color: "#1e293b" }}>
                        Select Assigner
                    </Typography>
                    <IconButton size="small" onClick={onClose} sx={{ color: "#94a3b8" }}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>

                <TextField
                    size="small"
                    fullWidth
                    placeholder="Search by name or email…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    autoFocus
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <FaSearch size={12} color="#94a3b8" />
                            </InputAdornment>
                        ),
                        sx: { borderRadius: "8px", fontSize: "0.8rem", height: "32px" },
                    }}
                />

                {/* "Me" row */}
                <Box
                    onClick={() => handleSelect(currentUserId)}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1,
                        py: 0.75,
                        borderRadius: "8px",
                        cursor: "pointer",
                        bgcolor: String(selectedId) === String(currentUserId) ? "#eff6ff" : "transparent",
                        border: String(selectedId) === String(currentUserId) ? "1px solid #bfdbfe" : "1px solid transparent",
                        "&:hover": { bgcolor: "#f8fafc" },
                    }}
                >
                    <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        badgeContent={
                            <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid #fff" }}>
                                <StarIcon sx={{ fontSize: 7, color: "#fff" }} />
                            </Box>
                        }
                    >
                        <Avatar sx={{ width: 28, height: 28, fontSize: "0.68rem", fontWeight: 700, bgcolor: "#e2e8f0", color: "#475569" }}>
                            me
                        </Avatar>
                    </Badge>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            Me (my tasks)
                        </Typography>
                    </Box>
                    {String(selectedId) === String(currentUserId) && (
                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#2563eb" }} />
                    )}
                </Box>

                {/* Assigner list */}
                <Box sx={{ maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 0.25 }}>
                    {filtered.length === 0 && (
                        <Typography sx={{ py: 1.5, textAlign: "center", color: "#94a3b8", fontSize: "0.78rem" }}>
                            No results found.
                        </Typography>
                    )}
                    {filtered.map((a) => {
                        const isSelected = String(selectedId) === a.id;
                        const name = `${a.firstName || ""} ${a.lastName || ""}`.trim() || a.email;
                        const bg = avatarBg(a.firstName, a.lastName, a.email);
                        const initials = buildInitials(a.firstName, a.lastName, a.email);
                        return (
                            <Box
                                key={a.id}
                                onClick={() => handleSelect(a.id)}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    px: 1,
                                    py: 0.75,
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    bgcolor: isSelected ? "#eff6ff" : "transparent",
                                    border: isSelected ? "1px solid #bfdbfe" : "1px solid transparent",
                                    "&:hover": { bgcolor: "#f8fafc" },
                                    transition: "background 0.12s",
                                }}
                            >
                                <Avatar sx={{ width: 28, height: 28, fontSize: "0.68rem", fontWeight: 700, bgcolor: bg, color: "#fff" }}>
                                    {initials}
                                </Avatar>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {name}
                                    </Typography>
                                    {a.email && (
                                        <Typography sx={{ fontSize: "0.68rem", color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {a.email}
                                        </Typography>
                                    )}
                                </Box>
                                {isSelected && (
                                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#2563eb", flexShrink: 0 }} />
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Popover>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
/**
 * "Created By" (Assigner) avatar filter.
 *
 * @param {Array}   assigners        - raw assigner list from facet API
 * @param {string}  selectedUserId   - currently selected user id
 * @param {string}  currentUserId    - logged-in user id (for "me" avatar)
 * @param {fn}      onSelect         - (userId) => void
 * @param {boolean} disabled
 */
const CreatedByFilter = ({
    assigners: rawAssigners = [],
    selectedUserId,
    currentUserId,
    onSelect,
    disabled = false,
    // legacy prop aliases kept for backward compat
    creators = [],
}) => {
    const [pickerAnchorEl, setPickerAnchorEl] = useState(null);

    // Normalise — accept both old `creators` shape and new facet `assigners` shape
    // Filter out current user from assigners list to avoid duplication next to 'me'
    const assigners = useMemo(() => {
        const source = rawAssigners.length > 0 ? rawAssigners : creators;
        const mapped = source
            .map((a) => normAssigner(a))
            .filter((a) => !currentUserId || String(a.id) !== String(currentUserId));

        if (selectedUserId && String(selectedUserId) !== String(currentUserId)) {
            const idx = mapped.findIndex((a) => String(a.id) === String(selectedUserId));
            if (idx > -1) {
                const [selectedUser] = mapped.splice(idx, 1);
                mapped.unshift(selectedUser);
            }
        }
        return mapped;
    }, [rawAssigners, creators, currentUserId, selectedUserId]);

    // Support comma-separated multi-selection
    const selectedIds = useMemo(() => {
        if (selectedUserId == null || selectedUserId === "") return [];
        return String(selectedUserId).split(",").map((s) => s.trim()).filter(Boolean);
    }, [selectedUserId]);

    const isMeSelected =
        currentUserId != null && selectedIds.includes(String(currentUserId));

    const handleMeClick = () => {
        if (disabled || !currentUserId) return;
        if (isMeSelected) {
            onSelect(null);
        } else {
            onSelect(String(currentUserId));
        }
    };

    const handlePersonClick = (a) => {
        if (disabled) return;
        if (selectedUserId != null && String(selectedUserId) === String(a.id)) {
            onSelect(null);
        } else {
            onSelect(a.id);
        }
    };

    const inline = assigners.slice(0, INLINE_MAX);
    const overflow = assigners.length > INLINE_MAX ? assigners.length - INLINE_MAX : 0;

    return (
        <>
            <Box className="created-by-filter" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 500, color: "#64748b", whiteSpace: "nowrap" }}>
                    Created By:
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {/* "Me" avatar */}
                    <Tooltip title="My tasks" arrow>
                        <Badge
                            overlap="circular"
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            badgeContent={
                                <Box sx={{ width: 14, height: 14, borderRadius: "50%", bgcolor: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid #fff" }}>
                                    <StarIcon sx={{ fontSize: 9, color: "#fff" }} />
                                </Box>
                            }
                        >
                            <Avatar
                                onClick={handleMeClick}
                                sx={{
                                    width: AVATAR_SIZE,
                                    height: AVATAR_SIZE,
                                    fontSize: "0.72rem",
                                    fontWeight: 600,
                                    bgcolor: "#e8eaed",
                                    color: "#475569",
                                    cursor: disabled ? "default" : "pointer",
                                    border: isMeSelected ? "2.5px solid #2563eb" : "2.5px solid transparent",
                                    boxShadow: isMeSelected ? "0 0 0 2px #bfdbfe" : "none",
                                    "&:hover": disabled ? {} : { bgcolor: "#dfe3e8" },
                                    transition: "box-shadow 0.15s, border-color 0.15s",
                                }}
                            >
                                me
                            </Avatar>
                        </Badge>
                    </Tooltip>

                    {/* Inline assigners (first INLINE_MAX) */}
                    {inline.map((a) => (
                        <PersonAvatar
                            key={a.id}
                            person={a}
                            isSelected={selectedIds.includes(a.id)}
                            onClick={() => handlePersonClick(a)}
                            disabled={disabled}
                        />
                    ))}

                    {/* +N overflow chip — opens full picker popover */}
                    {overflow > 0 && (
                        <Tooltip title={`View all ${assigners.length} assigners`} arrow>
                            <Avatar
                                onClick={(e) => !disabled && setPickerAnchorEl(e.currentTarget)}
                                sx={{
                                    width: AVATAR_SIZE,
                                    height: AVATAR_SIZE,
                                    fontSize: "0.68rem",
                                    fontWeight: 700,
                                    bgcolor: "#f1f5f9",
                                    color: "#475569",
                                    border: "2px solid #e2e8f0",
                                    cursor: disabled ? "default" : "pointer",
                                    "&:hover": disabled ? {} : { bgcolor: "#e2e8f0" },
                                }}
                            >
                                +{overflow}
                            </Avatar>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {/* Full assigner picker popover */}
            <AssignerPickerPopover
                open={Boolean(pickerAnchorEl)}
                anchorEl={pickerAnchorEl}
                onClose={() => setPickerAnchorEl(null)}
                assigners={assigners}
                selectedId={selectedUserId}
                currentUserId={currentUserId}
                onSelect={(id) => {
                    onSelect(id);
                    setPickerAnchorEl(null);
                }}
            />
        </>
    );
};

export default CreatedByFilter;
